"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import PasswordInput from "../components/auth/PasswordInput"
import SocialLoginButtons from "../components/auth/SocialLoginButtons"
import { validatePassword, getPasswordStrength } from "../utils/auth/passwordUtils"
import { AuthStatus, type PasswordStrength, type PasswordValidation } from "../types/auth"
import ThemeToggle from "../components/auth/ThemeToggle"

import { useAppDispatch, useAppSelector } from "../redux/hooks"
import { clearError, signUpUser } from "../redux/slices/authSlice"
import { toast } from "react-toastify"
import Loading from "../components/auth/Loading"
import { useGetEmailedCodeMutation } from "../redux/api/auth.api"

interface SignUpFormData {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
}

const SignUp: React.FC = () => {
  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [confirmTerms, setConfirmTerms] = useState(Boolean(searchParams.get('termsAccepted')))

  const { status, error } = useAppSelector((state) => state.auth)

  const [formData, setFormData] = useState<SignUpFormData>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  })

  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(true)
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>(null)
  const [validation, setValidation] = useState<PasswordValidation>({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  })

  const [successMessage, setSuccessMessage] = useState<string>("")
  const [code, setCode] = useState<string>("")
  const [emailSending, setEmailSending] = useState<boolean>(false)
  const [emailSent, setEmailSent] = useState<boolean>(false)

  useEffect(() => {
    if (error) {
      toast.error(error || "An error occurred. Please try again!!.")
    }
    if (successMessage) {
      toast.success(successMessage)
    }
  }, [error, successMessage])

  useEffect(() => {
    if (!formData.password) {
      setPasswordStrength(null)
      setValidation({
        hasMinLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSpecialChar: false,
      })
      return
    }
    const newValidation = validatePassword(formData.password)
    setValidation(newValidation)
    setPasswordStrength(getPasswordStrength(newValidation))

    if (formData.confirmPassword) {
      setPasswordsMatch(formData.password === formData.confirmPassword)
    }
  }, [formData.password, formData.confirmPassword])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const [getEmailedCode] = useGetEmailedCodeMutation()

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()

    if (!confirmTerms) {
      toast.error("Please confirm the terms and conditions.")
      return
    }
    if (!code) {
      toast.error("Please enter the code sent to your email.")
      return
    }
    if (!passwordsMatch || passwordStrength === "weak") {
      return
    }

    try {
      const newFormData = new FormData()
      newFormData.append("PrimaryEmail", formData.email)
      newFormData.append("Password", formData.password)
      newFormData.append("ConfirmPassWord", formData.confirmPassword)
      newFormData.append("FirstName", formData.firstName)
      newFormData.append("LastName", formData.lastName)
      newFormData.append("EmailVerificationCode", code)
      newFormData.append("ProfilePictureFile", "")

      const response = await dispatch(signUpUser(newFormData)).unwrap()

      if (response.token) {
        setSuccessMessage("User registered successfully.")
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          confirmPassword: "",
        })
        navigate("/login")
      }
    } catch (err) {
      setSuccessMessage("")
    }
  }

  const getPasswordStrengthBarStyles = () => {
    if (!formData.password) {
      return {
        width: "0%",
        className: "bg-transparent",
      }
    }

    let width = "0%"
    let color = "bg-gray-500"

    switch (passwordStrength) {
      case "weak":
        width = "33%"
        color = "bg-red-500"
        break
      case "medium":
        width = "66%"
        color = "bg-yellow-500"
        break
      case "strong":
        width = "100%"
        color = "bg-green-500"
        break
    }

    return { width, className: color }
  }

  const strengthBarStyles = getPasswordStrengthBarStyles()

  const handleGetEmailedCode = async (email: string) => {
    setEmailSending(true)
    try {
      await getEmailedCode(email).unwrap()
    } catch (error) {
      if (error && typeof error === "object" && "data" in error) {
        if (typeof error.data === "string") {
          if (error.data.includes("Successfully")) {
            setEmailSent(true)
            toast.success("Code sent successfully to your email.")
          } else {
            toast.error(typeof error.data === "string" ? error.data : "An error occurred. Please try again.")
          }
        }
      } else {
        toast.error("An error occurred. Please try again.")
      }
    } finally {
      setEmailSending(false)
    }
  }

  const isLoading = status === AuthStatus.LOADING;
  return (
    <div className="min-h-screen flex items-stretch md:items-center justify-center bg-white dark:bg-[#1a1a1a] md:py-16 text-gray-900 dark:text-white transition-all">
      <div className="absolute top-4 right-4 sm:relative sm:top-0 sm:right-0 ">
        <ThemeToggle />
      </div>
      <div className="w-full px-4 max-w-6xl min-h-full md:h-[600px] rounded-2xl overflow-hidden flex items-stretch flex-col md:flex-row">
        <div className="hidden  rounded-l-lg sm:flex sm:w-1/2  items-stretch justify-between">
          <img src="/login.jpg" className="w-full rounded-l-xl " alt="Illustration" />
        </div>

        <div className="w-full min-h-full md:w-1/2 p-6 rounded-2xl bg-gray-100 dark:bg-[#2a2a2a]">
          <div className="flex flex-col items-center mb-4">
            <div>
              <img src="./logo/green-logo.png" className="w-12 h-12 object-cover" alt="Logo" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-0 mt-2">Sign Up</h1>
            <p className="text-gray-400 dark:text-muted text-sm font-normal">Join us by creating a free account!</p>
          </div>

          <form onSubmit={handleSubmit} className="w-full px-4 sm:px-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
              {/* First name - full width on mobile, half width on sm and up */}
              <div className="col-span-1">
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  onChange={handleChange}
                  value={formData.firstName}
                  className="w-full p-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md dark:text-white text-sm text-gray-900 focus:ring-[#3FBBA3] focus:border-[#3FBBA3] dark:focus:ring-[#3FBBA3] dark:focus:border-[#3FBBA3]"
                  placeholder="First Name"
                  required
                />
              </div>

              {/* Last name - full width on mobile, half width on sm and up */}
              <div className="col-span-1">
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  onChange={handleChange}
                  value={formData.lastName}
                  className="w-full p-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white text-sm focus:ring-[#3FBBA3] focus:border-[#3FBBA3] dark:focus:ring-[#3FBBA3] dark:focus:border-[#3FBBA3]"
                  placeholder="Last Name"
                />
              </div>

              {/* Email - always full width */}
              <div className="col-span-1 sm:col-span-2">
                <input
                  type="email"
                  id="email"
                  name="email"
                  onChange={handleChange}
                  value={formData.email}
                  className="w-full p-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white text-sm focus:ring-[#3FBBA3] focus:border-[#3FBBA3] dark:focus:ring-[#3FBBA3] dark:focus:border-[#3FBBA3]"
                  placeholder="Email ID"
                  required
                />
              </div>

              {/* Password - full width on mobile, half width on sm and up */}
              <div className="col-span-1">
                <PasswordInput
                  id="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Password"
                  required
                />
                <div className="mt-2">
                  <div className="w-full h-2 dark:bg-gray-700 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${strengthBarStyles.className} transition-all duration-300`}
                      style={{ width: strengthBarStyles.width }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Confirm Password - full width on mobile, half width on sm and up */}
              <div className="col-span-1">
                <PasswordInput
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm Password"
                  hasError={!passwordsMatch && formData.confirmPassword.length > 0}
                  error={!passwordsMatch && formData.confirmPassword ? "Passwords do not match" : ""}
                  required
                />
              </div>

              {/* Verification code section - always full width */}
              <div className="col-span-1 sm:col-span-2 flex gap-2 sm:gap-4 items-center">
                <div className="flex-grow">
                  <input
                    type="text"
                    id="code"
                    onChange={(e) => setCode(e.target.value)}
                    value={code}
                    className="w-full p-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md dark:text-white text-sm text-gray-900 focus:ring-[#3FBBA3] focus:border-[#3FBBA3] dark:focus:ring-[#3FBBA3] dark:focus:border-[#3FBBA3]"
                    placeholder="Enter code"
                  />
                </div>
                <button
                  type="button"
                  className="whitespace-nowrap px-3 py-2 bg-[#3FBBA3] text-white rounded-md hover:bg-[#3FBBA3] transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) === null) {
                      toast.error("Please enter a valid email address.")
                      return
                    }
                    handleGetEmailedCode(formData.email)
                  }}
                  disabled={emailSending}
                >
                  {emailSent ? "resend" : "send"} <span className="hidden sm:inline">code</span>
                </button>
              </div>
            </div>
            <div className="flex items-center mb-2 mt-4">
              <input
                type="checkbox"
                name="agree"
                checked={confirmTerms}
                onChange={(e) => setConfirmTerms(e.target.checked)}
                className="mr-2"
              />
              <label className="text-sm">
                I agree to the
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 ml-2 underline"
                >
                  Terms & Conditions
                </a>
              </label>
            </div>

            <button
              type="submit"
              className={`w-full font-medium py-2.5 px-4 rounded-md transition duration-150 ease-in-out mt-3 ${passwordsMatch && passwordStrength === "strong"
                ? "bg-[#3FBBA3] hover:bg-[#3ebea5] text-white cursor-pointer"
                : "bg-[#3FBBA3] text-white cursor-not-allowed"
                } 
              disabled:opacity-50 disabled:cursor-not-allowed
              `}
              disabled={!passwordsMatch || passwordStrength !== "strong" || isLoading || emailSending}
            >
              {isLoading ? <Loading text="Signing up, please wait" /> : "Create Account"}
            </button>
          </form>
          {/* Terms and Conditions */}

          <SocialLoginButtons />

          <p className="mt-1 text-center text-xs text-gray-400">
            Already have an account?{" "}
            <Link
              onClick={() => {
                dispatch(clearError())
              }}
              to={isLoading ? "" : "/login"}
              className={`text-[#3FBBA3] hover:text-[#3FBBA3] ${isLoading ? "pointer-events-none text-accent-400" : ""}`}
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default SignUp
